/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */


import { CustomReportSubsidiaryStats } from '../../../types/custom-report';
import { Data, TableNode } from '@table-library/react-table-library/table';
import { Nullish } from '@table-library/react-table-library/common';
import { SurveyInitiative, SurveyType } from '../../../types/survey';
import { DATE, formatDateUTC } from '../../../utils/date';
import { DataPeriods } from '../../../types/universalTracker';


interface TreeNode {
  _id: string,
  parentId?: string
  leaf?: boolean;
  children?: TreeNode[],
  parent?: TreeNode
}

const createTreeGraph = (initiativeMap: Map<string, TreeNode>, parentId: string) => {

  const parentNode = initiativeMap.get(parentId);
  if (!parentNode) {
    return [];
  }

  const values = Array.from(initiativeMap.values());

  // Generate tree and create references
  let children = [parentNode]
  let nextChildren: TreeNode[] = [];

  while (children.length > 0) {
    nextChildren = []
    for (const c of children) {
      const innerChildren = values.filter(v => v.parentId === c._id)
      for (const inner of innerChildren) {
        inner.parent = c;
        nextChildren.push(inner);
      }
    }
    children = nextChildren;
  }

  return values;
}

export const getExpandedIds = (
  list: CustomReportSubsidiaryStats[],
  initiativeId: string,
  defaultSelected?: string[] | undefined,
): string[] => {

  if (!defaultSelected) {
    return [];
  }

  const initiativeMap = new Map<string, TreeNode>(list.map(i => {
    return [i._id, { _id: i._id, parentId: i.parentId }]
  }))

  const selectedIds = new Set(defaultSelected);
  const treeMap = createTreeGraph(initiativeMap, initiativeId);

  const leafNodes = treeMap.filter(node => selectedIds.has(node._id))

  const expendedIds = new Set<string>();

  // Traverse back from leaf nodes to the top
  leafNodes.forEach(n => {
    let parent = n.parent;
    while (parent) {

      if (expendedIds.has(parent._id)) {
        break; // Already visited this part of node
      }

      expendedIds.add(parent._id);
      parent = parent.parent;
    }
  })


  return Array.from(expendedIds);
}

const findSubsidiaries = (parentId: string, list: CustomReportSubsidiaryStats[], depth: number): Pick<Data, 'nodes'> | null => {
  const subsidiaries = list.filter(s => s.parentId === parentId);
  if (!subsidiaries || subsidiaries.length === 0) {
    return null;
  }

  return {
    nodes: subsidiaries.map(subsidiary => (
      {
        id: subsidiary._id,
        levelNum: depth,
        level: `Level ${depth}`,
        ...subsidiary,
        ...findSubsidiaries(subsidiary._id, list, depth + 1)
      }
    ))
  }
}

export const buildTree = (list: CustomReportSubsidiaryStats[], rootId: string): Data => {
  const rootInitiative = list.find(s => s._id === rootId);
  if (!rootInitiative) {
    return { nodes: [] };
  }

  return {
    nodes: [
      {
        id: rootInitiative._id,
        levelNum: 0,
        level: 'Level 0 (Organization)',
        ...rootInitiative,
        ...findSubsidiaries(rootInitiative._id, list, 1)
      }
    ]
  };
}

const getFlatNodes = (nodes: TableNode[], filterLevel: number, flatNodes: TableNode[]) => {
  nodes.forEach(node => {
    if (node.levelNum < filterLevel) {
      if (node.nodes) {
        getFlatNodes(node.nodes, filterLevel, flatNodes);
      }
    } else if (node.levelNum === filterLevel) {
      flatNodes.push({
        ...node,
        nodes: undefined
      });
    }
  });
}

export const buildList = (list: CustomReportSubsidiaryStats[], rootId: string, filterLevel: number): Data => {
  const tree = buildTree(list, rootId);

  const flatNodes: TableNode[] = [];
  getFlatNodes(tree.nodes, filterLevel, flatNodes);
  return {
    nodes: flatNodes
  }
}

export const filterData = (nodes: TableNode[] | Nullish, search: string): TableNode[] => {
  const isSearch = search && search.length >= 0;
  if (!nodes || !isSearch) {
    return nodes ?? [];
  }

  return nodes.map((node) => {
    if (node.name.toLowerCase().includes(search.toLowerCase())) {
      return node;
    }
    const filteredNodes = filterData(node.nodes, search);
    if (filteredNodes.length > 0) {
      return {
        ...node,
        nodes: filteredNodes
      };
    }
    return undefined;
  }).filter(node => node !== undefined) as TableNode[];
}

export const findMaxDepth = (list: CustomReportSubsidiaryStats[], initiativeId: string): number => {
  const initiativeMap = new Map<string, TreeNode>(
    list.map((i) => {
      return [i._id, { _id: i._id, parentId: i.parentId }];
    })
  );

  let maxNodeDepth = 0;
  const parentNode = initiativeMap.get(initiativeId);
  if (!parentNode) {
    return maxNodeDepth;
  }

  const values = Array.from(initiativeMap.values());

  let children = [parentNode];
  let nextChildren: TreeNode[] = [];

  while (children.length > 0) {
    nextChildren = [];
    for (const child of children) {
      const innerChildren = values.filter((v) => v.parentId === child._id);
      nextChildren.push(...innerChildren);
    }
    children = nextChildren;
    maxNodeDepth++;
  }
  return maxNodeDepth;
};

export const getSelectedSurveyNodeIds = (surveys?: Pick<SurveyInitiative, 'type' | 'effectiveDate' | 'period'>[]) => {
  if (!surveys) {
    return [];
  }

  return surveys.reduce((nodeIds, survey) => {
    const { type = SurveyType.Default, period = DataPeriods.Yearly, effectiveDate } = survey;
    if (!effectiveDate) {
      return nodeIds;
    }

    const monthYear = formatDateUTC(effectiveDate, DATE.MONTH_YEAR);
    const year = formatDateUTC(effectiveDate, DATE.YEAR_ONLY);
    nodeIds.push(`${year}-${type}-${monthYear}-${period}`);

    return nodeIds;
  }, [] as string[]);
};
